// Try-it demo (b) — the classification cascade. Pick one of four sample
// SAP rows and watch it fall through the five tiers, each card lighting up
// as it is checked. The mystery row is the headline: the AI proposes a line
// ("Planning Projects — Transfer Pricing, 62% confident"), you confirm or
// correct it in the review step, and a learned-mapping chip appears — AI
// proposes, a human confirms, the system learns. Canned data, no AI calls.

import React, { useEffect, useRef, useState } from 'react';
import useReducedMotion from './useReducedMotion';
import { fmtUSD } from './mockData';

const TIERS = [
  { n: 1, name: 'Matter code', sub: 'LG rows — Ascent code at the start of Text' },
  { n: 2, name: 'Invoice match', sub: 'row reconciled to an ERBI invoice' },
  { n: 3, name: 'Learned mapping', sub: 'vendor & text patterns it has learned' },
  { n: 4, name: 'AI suggestion', sub: 'reads the row, proposes a line' },
  { n: 5, name: 'Your review', sub: 'confirm or correct — it learns' },
];

const ROWS = [
  {
    id: 'lg',
    doc: '4700001231',
    type: 'LG',
    vendor: 'Ascent feed',
    text: '29157 Employment Credits',
    amt: 3200,
    hitTier: 1,
    result: { line: 'Employment Credits', how: 'Matter code 29157 — deterministic', conf: '100%' },
  },
  {
    id: 'inv',
    doc: '1900000456',
    type: 'KR',
    vendor: 'Deloitte',
    text: 'ref 9955',
    amt: 4000,
    hitTier: 2,
    result: { line: 'IRS Assistance', how: 'Inherited from matched invoice #9955', conf: '100%' },
  },
  {
    id: 'map',
    doc: '1900000789',
    type: 'KR',
    vendor: 'Gartner Inc',
    text: 'ANNUAL RESEARCH SEAT',
    amt: 8750,
    hitTier: 3,
    result: { line: 'Dues & Subscriptions', how: 'Learned vendor mapping — Gartner Inc', conf: '95%' },
  },
  {
    id: 'mystery',
    doc: '1900000912',
    type: 'KR',
    vendor: 'Baker & Co',
    text: 'TP DOCUMENTATION SVCS',
    amt: 7500,
    hitTier: 4,
    result: { line: 'Planning Projects — Transfer Pricing', how: 'AI suggestion', conf: '62%' },
    alt: 'Transfer Pricing Report - Canada',
  },
];

function buildFrames(row) {
  const frames = [];
  const tiers = {};
  TIERS.forEach((t) => {
    tiers[t.n] = 'idle';
  });
  const push = (extra = {}) =>
    frames.push({ tiers: { ...tiers }, showResult: false, review: false, ...extra });
  push();
  for (let t = 1; t <= row.hitTier; t += 1) {
    tiers[t] = 'checking';
    push();
    tiers[t] = t === row.hitTier ? 'hit' : 'miss';
    push();
  }
  if (row.hitTier === 4) {
    push({ showResult: true });
    tiers[5] = 'waiting';
    push({ showResult: true, review: true });
  } else {
    push({ showResult: true });
  }
  return frames;
}

export default function DemoCascade() {
  const reduced = useReducedMotion();
  const [rowId, setRowId] = useState(null);
  const [frame, setFrame] = useState(null);
  const [decision, setDecision] = useState(null); // { line, corrected }
  const timerRef = useRef(null);

  useEffect(() => () => timerRef.current && clearInterval(timerRef.current), []);

  const selectRow = (row) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setDecision(null);
    setRowId(row.id);
    const frames = buildFrames(row);
    if (reduced) {
      setFrame(frames[frames.length - 1]);
      return;
    }
    let i = 0;
    setFrame(frames[0]);
    timerRef.current = setInterval(() => {
      i += 1;
      if (i >= frames.length) {
        clearInterval(timerRef.current);
        return;
      }
      setFrame(frames[i]);
    }, 480);
  };

  const row = ROWS.find((r) => r.id === rowId);

  const tierState = (n) => {
    if (decision && n === 5) return 'hit';
    return frame ? frame.tiers[n] : 'idle';
  };

  const STATE_LABEL = {
    idle: '',
    checking: 'checking…',
    miss: 'no match',
    hit: 'match',
    waiting: 'your call',
  };

  return (
    <div className="tut-casc">
      <div className="tut-casc-pick">Pick a SAP row from the extract:</div>
      <div className="tut-casc-rows">
        {ROWS.map((r) => (
          <button
            key={r.id}
            type="button"
            className={`tut-sap-row${rowId === r.id ? ' sel' : ''}`}
            onClick={() => selectRow(r)}
          >
            <span className="doc code">
              {r.type} · {r.doc}
            </span>
            <span className="meta">
              {r.vendor} — {r.text}
            </span>
            <span className="amt num">{fmtUSD(r.amt)}</span>
          </button>
        ))}
      </div>

      <div className="tut-tiers">
        {TIERS.map((t) => {
          const st = tierState(t.n);
          return (
            <div key={t.n} className={`tut-tier ${st}`}>
              <span className="n num">{t.n}</span>
              <span className="nm">{t.name}</span>
              <span className="sub">{t.sub}</span>
              <span className="state">{STATE_LABEL[st]}</span>
            </div>
          );
        })}
      </div>

      {row && frame && frame.showResult && (
        <div className="tut-casc-result">
          {row.hitTier !== 4 && (
            <>
              <span className="lbl">Classified:</span>
              <span className="tut-chip line">{row.result.line}</span>
              <span className="how">{row.result.how}</span>
              <span className="tut-conf num">{row.result.conf}</span>
            </>
          )}
          {row.hitTier === 4 && (
            <>
              <span className="lbl">AI suggests:</span>
              <span className="tut-chip line">{row.result.line}</span>
              <span className="tut-conf num">62% confident</span>
              <span className="how">— too unsure to auto-apply, so it goes to your review queue</span>
            </>
          )}
        </div>
      )}

      {row && row.hitTier === 4 && frame && frame.review && !decision && (
        <div className="tut-casc-review">
          <span className="q">Review queue — is this Transfer Pricing?</span>
          <button
            type="button"
            className="tut-btn primary"
            onClick={() => setDecision({ line: row.result.line, corrected: false })}
          >
            Confirm — Transfer Pricing
          </button>
          <button
            type="button"
            className="tut-btn ghost"
            onClick={() => setDecision({ line: row.alt, corrected: true })}
          >
            No — it’s {row.alt}
          </button>
        </div>
      )}

      {row && decision && (
        <div className="tut-casc-learned">
          <span className="tut-chip learned">✓ learned mapping: Baker &amp; Co → {decision.line}</span>
          <span className="note">
            {decision.corrected
              ? 'Correction recorded — the AI’s confidence on this pattern drops, and your answer wins from now on.'
              : 'The next Baker & Co row classifies automatically at tier 3 — no AI needed.'}
          </span>
        </div>
      )}

      {!row && (
        <div className="tut-casc-hint">
          Tiers 1–3 are deterministic and free. The AI only reads rows nothing else could place —
          and it never gets the final word: you do.
        </div>
      )}
    </div>
  );
}
